import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const NEW_HIRE_AUTOMATION_TYPES = new Set(['document_request', 'document_send', 'form_collect', 'meeting_schedule']);
const PURE_AUTOMATION_TYPES = new Set(['scheduled_email']);
const SYSTEM_ASSIGNEE_LABELS = new Set(['system', 'system (auto)']);

/**
 * Task Node Component - Redesigned to match mockup
 * - Background: #568ded at 10% opacity
 * - Assignee: "FirstName L. (Dept)" format
 * - Completed: green border #40EB6E, checkmark replaces 3-dots, 40% content opacity
 */
const WorkflowTaskNode = ({
    task,
    isCompleted = false,
    onMenuAction,
    isInsideSharedCell = false,  // When true, don't render outer border (shared cell handles it)
    onToggleComplete,
    canManageTask = true,
    canToggleTaskCompletion = () => true
}) => {
  useUiLanguage();
    const { t } = useTranslation('onboarding');
    const [showMenu, setShowMenu] = useState(false);
    const [showUncheckConfirm, setShowUncheckConfirm] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    const getAutomationType = () => {
        if (task.automationType === 'document') return 'document_send';
        if (task.automationType === 'form') return 'form_collect';
        if (task.automationType === 'meeting') return 'meeting_schedule';
        if (task.automationType) return task.automationType;
        if (task.type === 'documentRequest' || task.portalUploadRequired || task.documentUploadLink) return 'document_request';
        if (task.formConfig || task.formLink) return 'form_collect';
        if (task.meetingConfig) return 'meeting_schedule';
        if ((task.type === 'automated' || task.type === 'document') && Array.isArray(task.attachments) && task.attachments.length > 0) return 'document_send';
        if (task.type === 'automated') return 'scheduled_email';
        return '';
    };

    const automationType = getAutomationType();
    // Determine task type for styling
    const taskType = task.type === 'automated' || automationType ? 'automated' : 'human';
    const isAutomated = taskType === 'automated';
    const completed = isCompleted || task.status === 'completed';
    const canMarkComplete = canToggleTaskCompletion?.(task, true) === true;
    const canUnmarkComplete = canToggleTaskCompletion?.(task, false) === true;

    // Get task icons based on type
    const getTaskIcons = () => {
        const icons = [];

        // Automation bolt icon
        if (isAutomated) {
            icons.push(<span key="auto" className="automation-icon" title={t('workflow.automated', 'Automated')}><i className="fa-solid fa-bolt"></i></span>);
        }

        // Email icon
        if (task.emailDraft || task.scheduledEmail || automationType === 'document_request' || automationType === 'document_send') {
            icons.push(<span key="email" className="automation-icon" title={t('workflow.email', 'Email')}><i className="fa-solid fa-envelope"></i></span>);
        }

        // Document icon
        if (automationType === 'document_send' || task.attachments?.length > 0) {
            icons.push(<span key="doc" className="automation-icon" title={t('workflow.document', 'Document')}><i className="fa-solid fa-file"></i></span>);
        }

        // Calendar/Meeting icon
        if (automationType === 'meeting_schedule' || task.meetingConfig) {
            icons.push(<span key="meeting" className="automation-icon" title={t('workflow.meeting', 'Meeting')}><i className="fa-solid fa-calendar"></i></span>);
        }

        // Form icon
        if (automationType === 'form_collect' || task.formConfig) {
            icons.push(<span key="form" className="automation-icon" title={t('workflow.form', 'Form')}><i className="fa-solid fa-list-check"></i></span>);
        }

        return icons;
    };

    // Format assignee display: "FirstName L. (Dept)" or "System (Auto)"
    const getAssigneeDisplay = () => {
        if (isAutomated && PURE_AUTOMATION_TYPES.has(automationType)) {
            return t('workflow.systemAuto', 'System (Auto)');
        }

        if (isAutomated && NEW_HIRE_AUTOMATION_TYPES.has(automationType)) {
            const assigneeName = String(task.assigneeName || '').trim();
            return assigneeName && !SYSTEM_ASSIGNEE_LABELS.has(assigneeName.toLowerCase())
                ? assigneeName
                : t('workflow.newHire', 'New Hire');
        }

        // If we have full name info
        if (task.assigneeFirstName && task.assigneeLastName) {
            const lastInitial = task.assigneeLastName.charAt(0).toUpperCase();
            const dept = task.assigneeDepartment || task.department || '';
            return `${task.assigneeFirstName} ${lastInitial}.${dept ? ` (${dept})` : ''}`;
        }

        // If we have assigneeName, try to parse it
        if (task.assigneeName) {
            const parts = task.assigneeName.trim().split(' ');
            if (parts.length >= 2) {
                const firstName = parts[0];
                const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
                const dept = task.assigneeDepartment || task.department || '';
                return `${firstName} ${lastInitial}.${dept ? ` (${dept})` : ''}`;
            }
            return task.assigneeName;
        }

        // Fallback to assignee field
        if (task.assignee) {
            // If it's an email, extract name portion
            if (task.assignee.includes('@')) {
                const emailName = task.assignee.split('@')[0];
                const nameParts = emailName.split(/[._-]/);
                if (nameParts.length >= 2) {
                    const firstName = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
                    const lastInitial = nameParts[1].charAt(0).toUpperCase();
                    const dept = task.assigneeDepartment || task.department || '';
                    return `${firstName} ${lastInitial}.${dept ? ` (${dept})` : ''}`;
                }
            }
            return task.assignee;
        }

        return t('tasks.assigneePlaceholder', 'Unassigned');
    };

    // Format due date
    const formatDueDate = () => {
        if (!task.dueDate) {
            if (task.executionMode === 'immediate') return t('workflow.immediate', 'Immediate');
            return t('workflow.noDate', 'No date');
        }
        // Parse as local date to avoid timezone shift (YYYY-MM-DD format)
        const parts = task.dueDate.split('-');
        const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return date.toLocaleDateString(getUiLocale(), { month: 'long', day: 'numeric' });
    };

    // Handle menu item click
    const handleMenuClick = (action) => {
        setShowMenu(false);
        onMenuAction?.(action, task);
    };

    // Handle checkmark click on completed task
    const handleCheckmarkClick = (e) => {
        e.stopPropagation();
        if (!canUnmarkComplete) return;
        setShowUncheckConfirm(true);
    };

    // Confirm unchecking the task
    const confirmUncheck = () => {
        setShowUncheckConfirm(false);
        onToggleComplete?.(task, false);
        onMenuAction?.('uncomplete', task);
    };

    // Get menu options based on task type
    const getMenuOptions = () => {
        const options = [];

        if (canManageTask) {
            options.push({ action: 'edit', icon: 'fa-pen', label: t('workflow.editTask', 'Edit Task') });
        }

        if (!completed && canMarkComplete) {
            options.push({ action: 'complete', icon: 'fa-check', label: t('workflow.markAsComplete', 'Mark as Complete') });
        }

        if (canManageTask) {
            // Document/upload option for document tasks
            if (automationType === 'document_request' || task.type === 'document' || task.type === 'documentRequest') {
                options.push({ action: 'upload_docs', icon: 'fa-upload', label: t('workflow.uploadDocuments', 'Upload Documents') });
            }

            // Email edit option
            if (task.emailDraft || task.scheduledEmail) {
                options.push({ action: 'edit_email', icon: 'fa-envelope', label: t('workflow.editEmailDraft', 'Edit Email Draft') });
            }

            options.push({ action: 'delete', icon: 'fa-trash', label: t('workflow.deleteTask', 'Delete Task'), danger: true });
        }

        return options;
    };
    const menuOptions = getMenuOptions();

    const nodeClasses = [
        'workflow-task-node',
        taskType,
        completed ? 'completed' : '',
        isInsideSharedCell ? 'inside-shared-cell' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className={nodeClasses} style={{ zIndex: showMenu ? 5000 : 'auto' }}>
            <div className={`task-node-content ${completed ? 'content-completed' : ''}`}>
                {/* Title row with icons */}
                <div className="task-title-row">
                    <span className="task-title">{task.title}</span>
                    <span className="task-icons">{getTaskIcons()}</span>
                </div>

                {/* Meta row - assignee and date */}
                <div className="task-meta-row">
                    <div
                        className="task-assignee"
                        title={canManageTask ? t('workflow.clickToEdit', 'Click to edit') : undefined}
                        onClick={canManageTask ? (e) => { e.stopPropagation(); onMenuAction?.('edit', task); } : undefined}
                    >
                        <i className="fa-solid fa-user" />
                        <span>{getAssigneeDisplay()}</span>
                    </div>
                    <div
                        className="task-date"
                        title={canManageTask ? t('workflow.clickToEditDate', 'Click to edit date') : undefined}
                        onClick={canManageTask ? (e) => { e.stopPropagation(); onMenuAction?.('edit', task); } : undefined}
                    >
                        <i className="fa-solid fa-calendar-day" />
                        <span>{formatDueDate()}</span>
                    </div>
                </div>
            </div>

            {/* Completed: show checkmark instead of 3-dots */}
            {completed ? (
                <button
                    className="task-complete-checkmark"
                    onClick={handleCheckmarkClick}
                    disabled={!canUnmarkComplete}
                    title={canUnmarkComplete ? t('workflow.clickToUnmarkComplete', 'Click to unmark as complete') : t('workflow.completed', 'Completed')}
                >
                    <i className="fa-solid fa-check" />
                </button>
            ) : menuOptions.length > 0 ? (
                /* Three-dot menu button */
                <button
                    className="task-menu-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(!showMenu);
                    }}
                >
                    <i className="fa-solid fa-ellipsis" />
                </button>
            ) : null}

            {/* Dropdown menu */}
            {showMenu && !completed && (
                <div className="task-action-menu" ref={menuRef}>
                    {menuOptions.map(option => (
                        <button
                            key={option.action}
                            className={`menu-item ${option.danger ? 'danger' : ''}`}
                            onClick={() => handleMenuClick(option.action)}
                        >
                            <i className={`fa-solid ${option.icon}`} />
                            {uiText(option.label)}
                        </button>
                    ))}
                </div>
            )}

            {/* Uncheck confirmation dialog */}
            {showUncheckConfirm && (
                <div className="uncheck-confirm-overlay" onClick={() => setShowUncheckConfirm(false)}>
                    <div className="uncheck-confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <p>{t('workflow.unmarkConfirm', 'Are you sure you want to unmark this task as complete?')}</p>
                        <div className="confirm-buttons">
                            <button className="btn-cancel" onClick={() => setShowUncheckConfirm(false)}>
                                {t('buttons.cancel', 'Cancel')}
                            </button>
                            <button className="btn-confirm" onClick={confirmUncheck}>
                                {t('workflow.yesUnmark', 'Yes, Unmark')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowTaskNode;
